import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorCode } from '@brimo/shared';
import { ApiError, api, errorMessage } from '../../lib/api';
import { Banner, Button, Card, Checkbox, CodeField, Eyebrow, TextField, TextLink } from '../../ds/components';
import { isUnauthenticated, useSessionQuery } from '../../lib/session';
import { AuthFrame } from './AuthFrame';
import { attemptsText, invalidateSession, useLogout } from './auth';
import styles from './LoginPage.module.css';

/** Shell 17: the second factor after the password — TOTP from the app or a recovery code. */
export function TwoFactorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSessionQuery();
  const logout = useLogout();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (session.isError && isUnauthenticated(session.error)) return <Navigate to="/login" replace />;
  if (session.data?.twoFactorPassed || session.data?.twoFactorRequired === false)
    return <Navigate to="/home" replace />;
  if (session.data && !session.data.account.twoFactorEnabled)
    return <Navigate to="/login/2fa/setup" replace />;

  async function verify(value: string) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api<{ next: 'workspace' }>('/auth/2fa/verify', {
        method: 'POST',
        body: { code: value, trustDevice: trust },
      });
      await invalidateSession(queryClient);
      await navigate('/home', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && err.code === ErrorCode.UNAUTHENTICATED) {
        queryClient.clear();
        await navigate('/login', { replace: true, state: { message: err.message, tone: 'danger' } });
        return;
      }
      setError(errorMessage(err));
      setAttempts(attemptsText(err));
      setCode('');
      setRecoveryCode('');
    } finally {
      setPending(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const value = recovery ? recoveryCode.trim() : code;
    if (!value) return;
    void verify(value);
  }

  async function otherAccount() {
    await logout.mutateAsync().catch(() => undefined);
    await navigate('/login', { replace: true });
  }

  return (
    <AuthFrame>
      <Card padded>
        <Eyebrow>Schritt 2 von 2</Eyebrow>
        <div className={styles.afterEyebrow}>
          <h1 className={styles.title}>{recovery ? 'Wiederherstellungscode' : 'Bestätigungscode'}</h1>
          <p className={styles.subtitle}>
            {recovery
              ? 'Geben Sie einen Ihrer zehn Wiederherstellungscodes ein. Jeder Code gilt einmal.'
              : 'Geben Sie den Code aus Ihrer Authenticator-App ein. Zwei-Faktor ist für alle internen Nutzer Pflicht.'}
          </p>
        </div>
        {error ? (
          <div className={styles.banner}>
            <Banner tone="danger">{error}</Banner>
          </div>
        ) : null}
        <form className={styles.form} onSubmit={submit} noValidate>
          {recovery ? (
            <TextField
              label="Wiederherstellungscode"
              name="recovery-code"
              autoComplete="one-time-code"
              placeholder="XXXX-XXXX"
              autoFocus
              value={recoveryCode}
              onChange={(event) => setRecoveryCode(event.target.value.toUpperCase())}
              invalid={Boolean(error)}
            />
          ) : (
            <CodeField
              label="Bestätigungscode"
              labelHidden
              name="code"
              autoFocus
              value={code}
              onChange={setCode}
              onComplete={(value) => void verify(value)}
              invalid={Boolean(error)}
              disabled={pending}
            />
          )}
          {attempts ? <div className={styles.attempts}>{attempts}</div> : null}
          <Checkbox
            label="Diesem Gerät 30 Tage vertrauen"
            checked={trust}
            onChange={(event) => setTrust(event.target.checked)}
          />
          <Button type="submit" block loading={pending}>
            Bestätigen
          </Button>
          <div className={styles.links}>
            <TextLink
              as="button"
              tone="muted"
              onClick={() => {
                setRecovery((value) => !value);
                setError(null);
                setCode('');
                setRecoveryCode('');
              }}
            >
              {recovery ? 'Code aus der App nutzen' : 'Wiederherstellungscode nutzen'}
            </TextLink>
            <TextLink as="button" tone="muted" onClick={() => void otherAccount()}>
              Anderes Konto
            </TextLink>
          </div>
        </form>
      </Card>
    </AuthFrame>
  );
}
